import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaTrash,
  FaPlus,
  FaMinus,
  FaTruck,
  FaStore,
  FaCreditCard,
} from "react-icons/fa";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useCart } from "../context/CartContext.tsx";
import StripeCheckout from "../components/StripeCheckout.tsx";
import { DeliveryInfo } from "../types/index.ts";
import { database } from "../firebase/config.ts";
import { ref, onValue } from "firebase/database";
import "./Order.css";

// Clé publique Stripe depuis .env
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLIC_KEY || "pk_test_51QYourPublicKeyHere"
);

interface DeliverySettings {
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  freeDeliveryThreshold: number;
  deliveryFee: number;
  maxDeliveryDistance: number;
}

const Order: React.FC = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getTotal, clearCart } =
    useCart();
  const [deliveryType, setDeliveryType] = useState<"delivery" | "pickup">(
    "delivery"
  );
  const [paymentMethod] = useState<"card">("card");
  const [showStripeModal, setShowStripeModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<DeliveryInfo>({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    notes: "",
    latitude: "",
    longitude: "",
  });
  const [gettingLocation, setGettingLocation] = useState<boolean>(false);
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>({
    deliveryEnabled: true,
    pickupEnabled: true,
    freeDeliveryThreshold: 20,
    deliveryFee: 3.50,
    maxDeliveryDistance: 10
  });
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);

  // Charger les paramètres de livraison depuis Firebase
  useEffect(() => {
    const settingsRef = ref(database, 'settings/delivery');
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setDeliverySettings(data);
        // Si la livraison est désactivée, basculer sur le retrait
        if (!data.deliveryEnabled && data.pickupEnabled) {
          setDeliveryType('pickup');
        }
        // Si le retrait est désactivé, basculer sur la livraison
        else if (!data.pickupEnabled && data.deliveryEnabled) {
          setDeliveryType('delivery');
        }
      }
      setSettingsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const deliveryFee = deliveryType === "delivery" 
    ? (getTotal() >= deliverySettings.freeDeliveryThreshold ? 0 : deliverySettings.deliveryFee) 
    : 0;
  const finalTotal = getTotal() + deliveryFee;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getMyLocation = () => {
    if (!navigator.geolocation) {
      alert("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        });
        setGettingLocation(false);
        alert("Position détectée avec succès ! 📍");
      },
      (error) => {
        setGettingLocation(false);
        let errorMessage = "Impossible de récupérer votre position";
        if (error.code === 1) {
          errorMessage = "Veuillez autoriser l'accès à votre position";
        } else if (error.code === 2) {
          errorMessage = "Position non disponible";
        } else if (error.code === 3) {
          errorMessage = "Délai d'attente dépassé";
        }
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Bouton cliqué !");

    if (cart.length === 0) {
      alert("Votre panier est vide");
      return;
    }

    // Vérifier que les modes de livraison sont disponibles
    if (!deliverySettings.deliveryEnabled && !deliverySettings.pickupEnabled) {
      alert("Aucun mode de livraison n'est disponible actuellement. Veuillez nous contacter.");
      return;
    }

    // Vérifier que le mode sélectionné est disponible
    if (deliveryType === "delivery" && !deliverySettings.deliveryEnabled) {
      alert("La livraison à domicile n'est pas disponible actuellement.");
      return;
    }

    if (deliveryType === "pickup" && !deliverySettings.pickupEnabled) {
      alert("Le retrait sur place n'est pas disponible actuellement.");
      return;
    }

    // Vérifier que les champs requis sont remplis
    if (!formData.name || !formData.phone || !formData.email) {
      alert(
        "Veuillez remplir tous les champs obligatoires (Nom, Téléphone, Email)"
      );
      return;
    }

    // Si livraison, vérifier l'adresse
    if (
      deliveryType === "delivery" &&
      (!formData.address || !formData.city || !formData.postalCode)
    ) {
      alert("Veuillez remplir l'adresse de livraison complète");
      return;
    }

    console.log("Validation OK, méthode de paiement:", paymentMethod);

    // Ouvrir Stripe pour le paiement par carte
    console.log("Ouverture modal Stripe...");
    setShowStripeModal(true);
  };

  const handleStripeSuccess = async (paymentMethodId: string) => {
    await finalizeOrder(paymentMethodId);
    setShowStripeModal(false);
  };

  const finalizeOrder = async (paymentMethodId: string | null = null) => {
    const orderData = {
      cart,
      deliveryType,
      paymentMethod,
      formData,
      total: finalTotal,
      orderDate: new Date().toISOString(),
      paymentStatus: paymentMethod === "card" ? "paid" : "pending",
      ...(paymentMethodId && { stripePaymentId: paymentMethodId }),
    };

    // Sauvegarder dans Firebase
    try {
      const { database } = await import("../firebase/config");
      const { ref, push } = await import("firebase/database");
      const ordersRef = ref(database, "orders");
      await push(ordersRef, orderData);
    } catch (error) {
      console.error("Erreur Firebase:", error);
    }

    localStorage.setItem("lastOrder", JSON.stringify(orderData));
    clearCart();
    navigate("/confirmation");
  };

  if (cart.length === 0) {
    return (
      <div className="order-page">
        <div className="empty-cart">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">🛒</div>
            <h2>Votre panier est vide</h2>
            <p>Découvrez notre menu et ajoutez des plats délicieux</p>
            <motion.button
              className="btn-primary"
              onClick={() => navigate("/")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Voir le Menu
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      <section className="order-hero">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Finaliser la Commande
        </motion.h1>
      </section>

      <div className="container">
        <div className="order-layout">
          <div className="order-main">
            <motion.div
              className="cart-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>Votre Panier</h2>
              {cart.map((item, index) => (
                <motion.div
                  key={`${item.id}-${index}`}
                  className="cart-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="cart-item-image">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="cart-item-details">
                    <h3>{item.name}</h3>
                    {item.options && item.options.length > 0 && (
                      <p className="cart-item-options">
                        {item.options.map((opt) => opt.name).join(", ")}
                      </p>
                    )}
                    <div className="cart-item-price">
                      {(
                        item.price +
                        (item.options?.reduce(
                          (sum, opt) => sum + opt.price,
                          0
                        ) || 0)
                      ).toFixed(2)}
                      €
                    </div>
                  </div>
                  <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.options,
                            item.quantity - 1
                          )
                        }
                      >
                        <FaMinus />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.options,
                            item.quantity + 1
                          )
                        }
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.id, item.options)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="delivery-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2>Mode de Livraison</h2>
              {settingsLoading ? (
                <div className="loading-delivery">
                  <p>Chargement des options de livraison...</p>
                </div>
              ) : (
                <div className="delivery-options">
                  {deliverySettings.deliveryEnabled && (
                    <label
                      className={`delivery-option ${
                        deliveryType === "delivery" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="delivery"
                        value="delivery"
                        checked={deliveryType === "delivery"}
                        onChange={(e) =>
                          setDeliveryType(e.target.value as "delivery" | "pickup")
                        }
                      />
                      <FaTruck />
                      <div>
                        <strong>Livraison à Domicile</strong>
                        <p>
                          {getTotal() >= deliverySettings.freeDeliveryThreshold 
                            ? "Gratuit" 
                            : `${deliverySettings.deliveryFee.toFixed(2)}€`}
                        </p>
                        {getTotal() < deliverySettings.freeDeliveryThreshold && (
                          <small>
                            Gratuit à partir de {deliverySettings.freeDeliveryThreshold}€
                          </small>
                        )}
                      </div>
                    </label>
                  )}
                  
                  {deliverySettings.pickupEnabled && (
                    <label
                      className={`delivery-option ${
                        deliveryType === "pickup" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="delivery"
                        value="pickup"
                        checked={deliveryType === "pickup"}
                        onChange={(e) =>
                          setDeliveryType(e.target.value as "delivery" | "pickup")
                        }
                      />
                      <FaStore />
                      <div>
                        <strong>Retrait sur Place</strong>
                        <p>Gratuit</p>
                        <small>81 Rue Bernard Palissy, 45500 Gien</small>
                      </div>
                    </label>
                  )}
                  
                  {!deliverySettings.deliveryEnabled && !deliverySettings.pickupEnabled && (
                    <div className="no-delivery-options">
                      <p>⚠️ Aucun mode de livraison disponible actuellement.</p>
                      <p>Veuillez nous contacter au 02 18 49 30 80</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            <motion.form
              className="order-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2>Informations de Contact</h2>
              <div className="form-grid">
                <input
                  type="text"
                  name="name"
                  placeholder="Nom complet *"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Téléphone *"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="full-width"
                />
              </div>

              {deliveryType === "delivery" && (
                <>
                  <h2>Adresse de Livraison</h2>
                  <div className="form-grid">
                    <input
                      type="text"
                      name="address"
                      placeholder="Adresse *"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="full-width"
                    />
                    <input
                      type="text"
                      name="city"
                      placeholder="Ville *"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                    <input
                      type="text"
                      name="postalCode"
                      placeholder="Code Postal *"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="location-section">
                    <h3>📍 Position GPS (optionnel)</h3>
                    <p className="location-help">
                      Partagez votre position pour faciliter la livraison
                    </p>
                    <motion.button
                      type="button"
                      className="btn-location"
                      onClick={getMyLocation}
                      disabled={gettingLocation}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {gettingLocation
                        ? "🔄 Détection en cours..."
                        : "📍 Détecter ma position"}
                    </motion.button>
                    {formData.latitude && formData.longitude && (
                      <div className="location-display">
                        <p>✅ Position enregistrée</p>
                        <small>
                          Lat: {formData.latitude}, Long: {formData.longitude}
                        </small>

                        {/* Carte interactive OpenStreetMap */}
                        <div className="map-container">
                          <iframe
                            title="Carte de livraison"
                            width="100%"
                            height="300"
                            frameBorder="0"
                            scrolling="no"
                            marginHeight={0}
                            marginWidth={0}
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                              parseFloat(formData.longitude) - 0.01
                            },${parseFloat(formData.latitude) - 0.01},${
                              parseFloat(formData.longitude) + 0.01
                            },${
                              parseFloat(formData.latitude) + 0.01
                            }&layer=mapnik&marker=${formData.latitude},${
                              formData.longitude
                            }`}
                            style={{ border: "none", borderRadius: "10px" }}
                          />
                        </div>

                        <a
                          href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-map-link"
                        >
                          🗺️ Ouvrir dans Google Maps
                        </a>
                      </div>
                    )}
                  </div>
                </>
              )}

              <h2>Mode de Paiement</h2>
              <div className="payment-options">
                <label className="payment-option active">
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={true}
                    readOnly
                  />
                  <FaCreditCard />
                  <span>Carte Bancaire</span>
                </label>
              </div>

              <textarea
                name="notes"
                placeholder="Notes pour la commande (optionnel)"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
              />
            </motion.form>
          </div>

          <motion.div
            className="order-summary"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2>Récapitulatif</h2>
            <div className="summary-line">
              <span>Sous-total</span>
              <span>{getTotal().toFixed(2)}€</span>
            </div>
            <div className="summary-line">
              <span>Livraison</span>
              <span>
                {deliveryFee === 0 ? "Gratuit" : `${deliveryFee.toFixed(2)}€`}
              </span>
            </div>
            {getTotal() >= deliverySettings.freeDeliveryThreshold && deliveryType === "delivery" && deliverySettings.deliveryEnabled && (
              <div className="free-delivery-badge">🎉 Livraison gratuite !</div>
            )}
            <div className="summary-total">
              <span>Total</span>
              <span>{finalTotal.toFixed(2)}€</span>
            </div>
            <motion.button
              type="submit"
              className="btn-primary full-width"
              onClick={handleSubmit}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Procéder au Paiement
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Modal Stripe */}
      <AnimatePresence>
        {showStripeModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowStripeModal(false)}
          >
            <motion.div
              className="modal-content stripe-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Elements stripe={stripePromise}>
                <StripeCheckout
                  amount={finalTotal}
                  onSuccess={handleStripeSuccess}
                  onCancel={() => setShowStripeModal(false)}
                />
              </Elements>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Order;
